import { dia } from '@clientio/jointjs'
import { getLinkAttrs, LinkLabelAttrs, minLabelSize } from './markup'

export const LinkOptCache = '_linkOptCache'

/**
 * Class representing a node link.
 * Extends the `dia.Link` class from JointJS.
 * @extends dia.Link
 */
export class NodeLink extends dia.Link {
  constructor(props) {
    super(props)

    // Add listener for source/target changes to clear cache
    this.on('change:source change:target', function () {
      // Clear cache on source/target change to force a clean reload
      this.unset(LinkOptCache, { silent: true })
    })
  }

  /**
   * Creates a new NodeLink with the specified source, target, and link data.
   * @param {string} source - The ID of the source element.
   * @param {string} [target] - The ID of the target element.
   * @param {WorkflowEdge} [linkData] - Additional data to cache in the link.
   * @returns {dia.Link} The created NodeLink instance.
   */
  static create(source, target, linkData) {
    const link = new NodeLink({
      ...getLinkAttrs(),
      id: linkData?.edge_uid,
      source: { id: source, selector: 'outport' },
      target: target ? { id: target, selector: 'inport' } : undefined,
      metadata: linkData?.metadata || {}
    })

    if (linkData) {
      link.setCachedLinkData(linkData)
    }

    link.setLabel(linkData?.label || '', linkData?.subLabel || '')

    return link
  }

  /**
   * Creates a new NodeLink from a magnet with the specified source.
   * @param {string} source - The ID of the source element.
   * @returns {dia.Link} The created NodeLink instance.
   */
  static createFromMagnet(source) {
    return NodeLink.create(source)
  }

  /**
   * Updates the link data with the given options.
   * If the label, source, or target has changed, it updates the link accordingly.
   * @param {WorkflowEdge} opt - The options to update the link with.
   */
  updateLinkData(opt) {
    // Early return if no options provided
    if (!opt) {
      return
    }

    // Get cached link data with fallback to empty object
    const prevOpt = this.getCachedLinkData() || {}

    // Force target update if different or not previously set
    if (opt.target && (!prevOpt.target || prevOpt.target !== opt.target)) {
      this.target({ id: opt.target, selector: 'inport' })

      // Clear the cache to force refresh
      this.unset(LinkOptCache, { silent: true })
    }

    // Update other properties
    if (prevOpt.label !== opt.label) {
      this.setLabel(opt.label, opt?.subLabel)
    }

    if (opt.source && (!prevOpt.source || prevOpt.source !== opt.source)) {
      this.source({ id: opt.source, selector: 'outport' })
    }

    // Always update cache after changes
    this.setCachedLinkData(opt)
  }

  /**
   * Sets the label text for the link. If the text is empty, the label is hidden.
   * @param {string} text - The text to set as the label.
   */
  setLabel(text, chip) {
    // parse chip to string
    chip = typeof chip === 'string' ? chip : chip?.toString()
    const chipWidth = chip ? Math.max(28, chip.length * 8 + 12) : 28

    this.label(0, {
      attrs: {
        label: { text },
        chip: {
          display: chip ? 'visible' : 'none',
          width: `${chipWidth}px`
        },
        chipText: {
          display: chip ? 'visible' : 'none',
          text: chip,
          refX: `${chipWidth / 2}px`
        },
        root: {
          class: `joint-link-label ${text ? 'label-visible' : 'label-hidden'}`,
          'data-tooltip': text
        }
      }
    })
    this.set('labelSize', text ? LinkLabelAttrs : minLabelSize)
  }

  /**
   * Gets the label text for the link.
   * @returns {string} The text of the label.
   */
  getLabelText() {
    return this.label(0)?.attrs.label.text
  }

  /**
   * Gets the cached link data.
   * @returns {WorkflowEdge} The cached link data.
   */
  getCachedLinkData() {
    return this.get(LinkOptCache)
  }

  /**
   * Sets the cached link data.
   * @param {WorkflowEdge} data - The data to cache in the link.
   */
  setCachedLinkData(data) {
    this.set(LinkOptCache, data, { replace: true, silent: true })
  }
}
